/**
 * Add your own mailbox, or change or remove one you added.
 *
 * Three fields a person fills in: the address, the name on their letters, the
 * password. The servers come filled in with the shared mailbox's (the same
 * provider, as a rule) and sit folded under "Server settings": somebody adding
 * kristi@ next to info@ should never have to know what IMAP is. The TLS mode
 * follows the port, as it does in the shared mailbox's settings.
 *
 * Saving tries the login first (the server does it) and says which half
 * failed and the mail server's own reason, so a mistyped password is an
 * answer here rather than an empty inbox later.
 */
import { ChevronDown, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { ApiError } from "../../../../lib/api";
import {
  addOwnMailbox,
  getOwnMailbox,
  removeOwnMailbox,
  updateOwnMailbox,
  type MailServerDefaults,
  type OwnMailboxForm,
} from "../../../../lib/mail";
import { Btn, Modal } from "../../../ui";
import { ConfirmModal } from "../../_shared/ConfirmModal";
import { smtpSecureForPort } from "../../settings/integrations/smtpPort";

const EMPTY_SERVERS: MailServerDefaults = {
  imapHost: "",
  imapPort: 993,
  imapSecure: true,
  smtpHost: "",
  smtpPort: 587,
  smtpSecure: false,
};

/** Implicit TLS on 993, STARTTLS on 143; any other port keeps what it had. */
const imapSecureForPort = (port: number, current: boolean) => (port === 993 ? true : port === 143 ? false : current);

export function MailboxDialog({
  mailboxId,
  defaults,
  onClose,
  onDone,
  onToast,
}: {
  /** Null: add a new one. */
  mailboxId: string | null;
  defaults: MailServerDefaults | null;
  onClose: () => void;
  /** Saved or removed; `removed` is the id of a mailbox that is gone. */
  onDone: (removed: string | null) => void;
  onToast: (msg: string) => void;
}) {
  const t = useT();
  const [form, setForm] = useState<OwnMailboxForm>({
    ...(defaults ?? EMPTY_SERVERS),
    address: "",
    name: "",
    login: "",
    pass: "",
  });
  const [passSet, setPassSet] = useState(false);
  // Open from the start when there is nothing to prefill: the host fields
  // are required, and a folded required field cannot say it is empty.
  const [servers, setServers] = useState(!defaults?.imapHost || !defaults.smtpHost);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (!mailboxId) return;
    let alive = true;
    getOwnMailbox(mailboxId)
      .then((row) => {
        if (!alive) return;
        setForm({ ...row, pass: "" });
        setPassSet(row.passSet);
      })
      .catch(() => alive && setError(t("mail.boxForm.failed")));
    return () => {
      alive = false;
    };
  }, [mailboxId, t]);

  const set = (patch: Partial<OwnMailboxForm>) => setForm((f) => ({ ...f, ...patch }));

  function reasonOf(err: unknown): string {
    const body = err instanceof ApiError ? (err.body as { error?: string; step?: string; reason?: string } | null) : null;
    if (body?.error === "login_failed") {
      return t(body.step === "smtp" ? "mail.boxForm.failedSmtp" : "mail.boxForm.failedImap", { reason: body.reason ?? "-" });
    }
    if (body?.error === "duplicate") return t("mail.boxForm.duplicate");
    if (body?.error === "shared") return t("mail.boxForm.shared", { address: body.reason ?? "" });
    if (body?.error === "too_many") return t("mail.boxForm.tooMany");
    return t("mail.boxForm.failed");
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body: OwnMailboxForm = {
      ...form,
      imapSecure: imapSecureForPort(Number(form.imapPort), form.imapSecure),
      smtpSecure: smtpSecureForPort(Number(form.smtpPort), form.smtpSecure),
      imapPort: Number(form.imapPort),
      smtpPort: Number(form.smtpPort),
    };
    try {
      if (mailboxId) await updateOwnMailbox(mailboxId, body);
      else await addOwnMailbox(body);
      onToast(t(mailboxId ? "mail.boxForm.saved" : "mail.boxForm.added"));
      onDone(null);
    } catch (err) {
      setError(reasonOf(err));
      // A refused login is often the server settings, which are folded away.
      const step = err instanceof ApiError ? (err.body as { step?: string } | null)?.step : undefined;
      if (step) setServers(true);
    } finally {
      setBusy(false);
    }
  }

  const field = (key: keyof OwnMailboxForm, label: string, extra: { type?: string; placeholder?: string; hint?: string; required?: boolean; auto?: string } = {}) => (
    <div>
      <span className="crm-lab">
        {label}
        {extra.required ? " *" : ""}
      </span>
      <label className="crm-inp">
        <input
          type={extra.type ?? "text"}
          value={String(form[key] ?? "")}
          onChange={(e) => set({ [key]: extra.type === "number" ? Number(e.target.value) : e.target.value } as Partial<OwnMailboxForm>)}
          placeholder={extra.placeholder}
          autoComplete={extra.auto ?? "off"}
          required={extra.required}
        />
      </label>
      {extra.hint ? <span className="crm-sub !mt-1.5 block text-[11.5px] leading-snug">{extra.hint}</span> : null}
    </div>
  );

  return (
    <>
      <Modal
        title={t(mailboxId ? "mail.boxForm.editTitle" : "mail.boxForm.addTitle")}
        label={t(mailboxId ? "mail.boxForm.editTitle" : "mail.boxForm.addTitle")}
        onClose={onClose}
        className="max-w-[460px]"
      >
        <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
          <p className="crm-sub text-[12.5px] leading-snug">{t("mail.boxForm.lead")}</p>
          {field("address", t("mail.boxForm.address"), { type: "email", required: true, auto: "email", placeholder: t("mail.boxForm.addressPlaceholder") })}
          {field("name", t("mail.boxForm.name"), { placeholder: t("mail.boxForm.namePlaceholder"), auto: "name" })}
          {field("pass", t("mail.boxForm.pass"), {
            type: "password",
            required: !mailboxId,
            auto: "new-password",
            hint: mailboxId && passSet ? t("mail.boxForm.passKeep") : undefined,
          })}

          <button
            type="button"
            className="flex items-center gap-1.5 self-start text-[12.5px] font-[650] text-muted-foreground hover:text-foreground"
            aria-expanded={servers}
            onClick={() => setServers((v) => !v)}
          >
            <ChevronDown className={cn("size-3.5 transition-transform", !servers && "-rotate-90")} />
            {t("mail.boxForm.server")}
          </button>
          {servers ? (
            <div className="flex flex-col gap-3 rounded-xl border border-[var(--crm-hair)] p-3">
              <span className="crm-sub text-[11.5px] leading-snug">{t("mail.boxForm.serverHint")}</span>
              {field("login", t("mail.boxForm.login"), { hint: t("mail.boxForm.loginHint"), auto: "username" })}
              <div className="grid grid-cols-[1fr_88px] gap-2">
                {field("imapHost", t("mail.boxForm.imapHost"), { required: true })}
                {field("imapPort", t("mail.boxForm.imapPort"), { type: "number", required: true })}
              </div>
              <div className="grid grid-cols-[1fr_88px] gap-2">
                {field("smtpHost", t("mail.boxForm.smtpHost"), { required: true })}
                {field("smtpPort", t("mail.boxForm.smtpPort"), { type: "number", required: true })}
              </div>
            </div>
          ) : null}

          {busy ? <p className="crm-sub text-[12.5px]">{t("mail.boxForm.testing")}</p> : null}
          {error ? <p className="text-[12.5px] leading-snug text-destructive">{error}</p> : null}

          <div className="crm-m-foot">
            {mailboxId ? (
              <Btn type="button" tone="danger" onClick={() => setRemoving(true)} disabled={busy} className="mr-auto">
                <Trash2 className="size-3.5" />
                {t("mail.boxForm.remove")}
              </Btn>
            ) : null}
            <Btn type="button" onClick={onClose} disabled={busy}>
              {t("common.cancel")}
            </Btn>
            <Btn type="submit" tone="dark" disabled={busy || !form.address.trim() || (!mailboxId && !form.pass)}>
              {busy ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : mailboxId ? (
                <Save className="size-3.5" />
              ) : (
                <Plus className="size-3.5" />
              )}
              {t(mailboxId ? "mail.boxForm.save" : "mail.boxForm.add")}
            </Btn>
          </div>
        </form>
      </Modal>

      {removing && mailboxId ? (
        <ConfirmModal
          title={t("mail.boxForm.removeTitle")}
          body={t("mail.boxForm.removeBody")}
          confirmLabel={t("mail.boxForm.remove")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await removeOwnMailbox(mailboxId);
            setRemoving(false);
            onToast(t("mail.boxForm.removed"));
            onDone(mailboxId);
          }}
          onClose={() => setRemoving(false)}
        />
      ) : null}
    </>
  );
}
